import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  offboardingControllerAddNotificationV1,
  offboardingControllerAddSettlementV1,
  offboardingControllerArchiveAllV1,
  offboardingControllerArchiveV1,
  offboardingControllerCancelV1,
  offboardingControllerCompleteV1,
  offboardingControllerFindOneV1,
  offboardingControllerListV1,
  offboardingControllerRecordTerminationV1,
  offboardingControllerRemoveSettlementV1,
  offboardingControllerStartV1,
  offboardingControllerSubmitPendingV1,
  offboardingControllerUpdateNotificationV1,
  offboardingControllerUpdateRetentionV1,
  offboardingControllerUpdateSettlementV1,
} from '@/api/generated/offboarding/offboarding';
import { tasksControllerUpdateTaskV1 } from '@/api/generated/tasks/tasks';
import { taskKeys } from '@/modules/tasks';
import { auditLogKeys } from '@/shared/lib/auditLogKeys';
import type {
  CreateNotificationDto,
  CreateSettlementDto,
  OffboardingDetailDto,
  RecordTerminationDto,
  UpdateNotificationDto,
  UpdateRetentionDto,
  UpdateSettlementDto,
  UpdateTaskDto,
} from '@/api/generated/model';
import { playerKeys } from '@/modules/players';
import { contractKeys } from '@/shared/lib/contractKeys';

/**
 * Query keys for the offboarding module.
 *
 * The same hierarchy as `onboardingKeys`, for the same reason: the API returns
 * tasks, settlements, notifications and retention records in one response, so
 * there is one cache entry per offboarding rather than four that can disagree
 * with each other mid-render.
 */
export const offboardingKeys = {
  all: ['offboardings'] as const,
  lists: () => [...offboardingKeys.all, 'list'] as const,
  list: () => [...offboardingKeys.lists(), 'default'] as const,
  details: () => [...offboardingKeys.all, 'detail'] as const,
  detail: (id: string) => [...offboardingKeys.details(), id] as const,
  /** The shared audit panel owns this query; this just names its key. */
  audit: (id: string) => auditLogKeys.forSubject('player_offboarding', id),
  /** The active offboarding for one player, for the player page's tab. */
  forPlayer: (playerId: string) => [...offboardingKeys.all, 'player', playerId] as const,
};

export function useOffboardingsQuery() {
  return useQuery({
    queryKey: offboardingKeys.list(),
    queryFn: ({ signal }) => offboardingControllerListV1({ limit: 50 }, { signal }),
  });
}

export function useOffboardingQuery(id: string | undefined) {
  return useQuery({
    queryKey: offboardingKeys.detail(id ?? ''),
    queryFn: ({ signal }) => offboardingControllerFindOneV1(id as string, { signal }),
    enabled: Boolean(id),
  });
}

/**
 * The offboarding for one player, if there is one.
 *
 * The player page's Offboarding tab knows a player id, not an offboarding id.
 * There is no endpoint that takes a player id, so this reads the list — which
 * the screen loads anyway — and picks the row out. A dedicated endpoint would
 * be worth adding once the list grows past one page; until then this avoids a
 * round trip rather than costing one.
 *
 * Returns the MOST RECENT offboarding rather than only an in-progress one: a
 * player who was offboarded and re-signed should still show the record of the
 * last parting on their page.
 */
export function usePlayerOffboardingQuery(playerId: string | undefined) {
  const offboardings = useOffboardingsQuery();

  const summary = playerId
    ? (offboardings.data?.data ?? []).find((offboarding) => offboarding.player.id === playerId)
    : undefined;

  const detail = useOffboardingQuery(summary?.id);

  return {
    summary,
    detail,
    // Pending only while we still might find one. Once the list has loaded and
    // there is no row, this is settled — the tab should show "not started",
    // not a spinner that never resolves.
    isPending: offboardings.isPending || (Boolean(summary) && detail.isPending),
    error: offboardings.error ?? detail.error,
  };
}

/**
 * Shared success handling for every offboarding write.
 *
 * All of them return the complete `OffboardingDetailDto`, so the response is
 * written straight into the detail cache — no refetch, and the screen never
 * flickers through a loading state after a save. This works only because the
 * API keeps that promise; if a new endpoint returns something narrower, it
 * needs its own handler rather than this one.
 */
function useOffboardingMutationEffects() {
  const queryClient = useQueryClient();

  return async (detail: OffboardingDetailDto) => {
    queryClient.setQueryData(offboardingKeys.detail(detail.id), detail);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: offboardingKeys.lists() }),
      queryClient.invalidateQueries({
        queryKey: offboardingKeys.audit(detail.id),
      }),
    ]);
  };
}

export function useStartOffboardingMutation() {
  const queryClient = useQueryClient();
  const onDetail = useOffboardingMutationEffects();

  return useMutation({
    mutationFn: (playerId: string) => offboardingControllerStartV1({ playerId }),
    onSuccess: async (detail) => {
      await onDetail(detail);
      await queryClient.invalidateQueries({ queryKey: playerKeys.lists() });
    },
  });
}

export function useCancelOffboardingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => offboardingControllerCancelV1(id),
    onSuccess: async (_result, id) => {
      // Dropped rather than invalidated: refetching an offboarding that no
      // longer exists only produces a 404.
      queryClient.removeQueries({ queryKey: offboardingKeys.detail(id) });
      await queryClient.invalidateQueries({ queryKey: offboardingKeys.lists() });
    },
  });
}

/**
 * Ends the representation.
 *
 * Also invalidates the player caches, because this is the one offboarding
 * action that changes the player record — `representation` becomes
 * `unrepresented`, and the player page and list both show it.
 */
export function useCompleteOffboardingMutation() {
  const queryClient = useQueryClient();
  const onDetail = useOffboardingMutationEffects();

  return useMutation({
    mutationFn: (id: string) => offboardingControllerCompleteV1(id),
    onSuccess: async (detail) => {
      await onDetail(detail);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: playerKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: playerKeys.detail(detail.player.id),
        }),
      ]);
    },
  });
}

/**
 * Task changes go through the generic tasks endpoint, because a task is a task
 * whether or not it belongs to a checklist. It returns the task rather than the
 * whole offboarding, so the detail is invalidated instead of written.
 */
export function useUpdateOffboardingTaskMutation(offboardingId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateTaskDto }) =>
      tasksControllerUpdateTaskV1(id, dto),
    onSuccess: async () => {
      await Promise.all([
        // Completing a step changes the lock state of the next one, the
        // completion percentage and `canComplete`, so the whole detail is
        // refetched rather than patched.
        queryClient.invalidateQueries({
          queryKey: offboardingKeys.detail(offboardingId),
        }),
        queryClient.invalidateQueries({ queryKey: offboardingKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: offboardingKeys.audit(offboardingId),
        }),
        // Checklist steps are tasks, so the Tasks board shows them too.
        queryClient.invalidateQueries({ queryKey: taskKeys.all }),
      ]);
    },
  });
}

// ── Step 1 ─────────────────────────────────────────────────────────────────

export function useRecordTerminationMutation(id: string) {
  const onDetail = useOffboardingMutationEffects();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: RecordTerminationDto) => offboardingControllerRecordTerminationV1(id, dto),
    onSuccess: async (detail) => {
      await onDetail(detail);
      // Recording an effective date terminates the player's representation
      // contract in the inventory, so its lists and tiles move too.
      await queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
    },
  });
}

// ── Step 2 ─────────────────────────────────────────────────────────────────

export function useAddSettlementMutation(id: string) {
  const onDetail = useOffboardingMutationEffects();

  return useMutation({
    mutationFn: (dto: CreateSettlementDto) => offboardingControllerAddSettlementV1(id, dto),
    onSuccess: onDetail,
  });
}

export function useUpdateSettlementMutation(id: string) {
  const onDetail = useOffboardingMutationEffects();

  return useMutation({
    mutationFn: ({ settlementId, dto }: { settlementId: string; dto: UpdateSettlementDto }) =>
      offboardingControllerUpdateSettlementV1(id, settlementId, dto),
    onSuccess: onDetail,
  });
}

export function useRemoveSettlementMutation(id: string) {
  const onDetail = useOffboardingMutationEffects();

  return useMutation({
    mutationFn: (settlementId: string) => offboardingControllerRemoveSettlementV1(id, settlementId),
    onSuccess: onDetail,
  });
}

// ── Step 3 ─────────────────────────────────────────────────────────────────

export function useAddNotificationMutation(id: string) {
  const onDetail = useOffboardingMutationEffects();

  return useMutation({
    mutationFn: (dto: CreateNotificationDto) => offboardingControllerAddNotificationV1(id, dto),
    onSuccess: onDetail,
  });
}

export function useUpdateNotificationMutation(id: string) {
  const onDetail = useOffboardingMutationEffects();

  return useMutation({
    mutationFn: ({ notificationId, dto }: { notificationId: string; dto: UpdateNotificationDto }) =>
      offboardingControllerUpdateNotificationV1(id, notificationId, dto),
    onSuccess: onDetail,
  });
}

export function useSubmitPendingNotificationsMutation(id: string) {
  const onDetail = useOffboardingMutationEffects();

  return useMutation({
    mutationFn: () => offboardingControllerSubmitPendingV1(id),
    onSuccess: onDetail,
  });
}

// ── Step 4 ─────────────────────────────────────────────────────────────────

export function useUpdateRetentionMutation(id: string) {
  const onDetail = useOffboardingMutationEffects();

  return useMutation({
    mutationFn: ({ retentionId, dto }: { retentionId: string; dto: UpdateRetentionDto }) =>
      offboardingControllerUpdateRetentionV1(id, retentionId, dto),
    onSuccess: onDetail,
  });
}

export function useArchiveRetentionMutation(id: string) {
  const onDetail = useOffboardingMutationEffects();

  return useMutation({
    mutationFn: (retentionId: string) => offboardingControllerArchiveV1(id, retentionId),
    onSuccess: onDetail,
  });
}

export function useArchiveAllMutation(id: string) {
  const onDetail = useOffboardingMutationEffects();

  return useMutation({
    mutationFn: () => offboardingControllerArchiveAllV1(id),
    onSuccess: onDetail,
  });
}
